import React, {FunctionComponent, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {IStore} from '../../redux/interface';
import {useIntl} from 'react-intl';
import {ICmFilter} from '../../redux/cm-service/interfaces';
import DmFilter from './dm-filter';
import {CmServiceFilterType} from './CmFilterEnums';
import {
  favoriteCmFiltersLoadedAction,
  selectedCmFiltersLoadedAction,
  stringCmFiltersLoadedAction
} from '../../redux/cm-service/actions';
import {CmFilterGroupsModel, CmFilterModel} from './CmFilterModels';
import Input from 'components/input';

import './styles.scss';

interface ICmResourceFilters {
  filters: ICmFilter[] | null
  onChange: (filters: ICmFilter[]) => void
}

const isCmFilter = (x: ICmFilter | null): x is ICmFilter => x !== null;

function buildSubcategoryGroups(filter: ICmFilter): CmFilterGroupsModel[] {
  return Object.values(
    (filter.allowValues || []).reduce<Record<number, CmFilterGroupsModel>>((acc, item) => {
      if (item.parentId) {
        if (!acc[item.parentId]) {
          acc[item.parentId] = {id: item.parentId, name: item.parentName || '', values: []};
        }

        acc[item.parentId].values.push({id: item.id, name: item.name, parentId: item.parentId, checked: false});
      }

      return acc;
    }, {})
  );
}

function buildListFilterModel(filter: ICmFilter): CmFilterModel {
  return {
    serialNumber: filter.serialNumber,
    name: filter.name,
    values: filter.allowValues ? filter.allowValues.map(v => ({id: v.id, name: v.name, parentId: v.parentId, checked: false})) : [],
    expanded: false
  };
}

function getCheckedValues(f: CmFilterModel): string {
  return f.values?.filter(v => v.checked).map(v => v.id).join(',')
    || f.groups?.flatMap(g => g.values).filter(v => v.checked).map(v => v.id).join(',') || '';
}

export const DmResourceFilters: FunctionComponent<ICmResourceFilters> = ({
  filters,
  onChange
}: ICmResourceFilters) => {
  const {
    selectedFilters, searchStringFilter, favoritesFilter
  } = useSelector((state: IStore) => state.cm_service);
  const dispatch = useDispatch();
  const intl = useIntl();
  const [searchString, setSearchString] = useState<string>(searchStringFilter?.inputValue || '');
  const [countyDropdownOpen, setCountyDropdownOpen] = useState(false);
  const [countySearchText, setCountySearchText] = useState('');
  const countyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (countyRef.current && !countyRef.current.contains(e.target as Node)) {
        setCountyDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (filters) {
      const models = {...selectedFilters};

      filters.filter(f => f.format === 'LIST').forEach((filter) => {
        const prevModel = models[filter.name];

        if (filter.name === CmServiceFilterType.cmSubcategories) {
          const checkedById = new Map(
            prevModel?.groups?.flatMap(g => g.values).map(v => [v.id, v.checked]) ?? []
          );

          models[filter.name] = {
            serialNumber: filter.serialNumber,
            name: filter.name,
            groups: buildSubcategoryGroups(filter).map(g => ({
              ...g,
              values: g.values.map(v => ({...v, checked: checkedById.get(v.id) ?? false}))
            })),
            expanded: prevModel?.expanded ?? false
          };
        } else {
          const checkedById = new Map(prevModel?.values?.map(v => [v.id, v.checked]) ?? []);
          const model = buildListFilterModel(filter);

          models[filter.name] = {
            ...model,
            values: (model.values ?? []).map(v => ({...v, checked: checkedById.get(v.id) ?? false})),
            expanded: prevModel?.expanded ?? false
          };
        }
      });

      if (!searchStringFilter?.name) {
        dispatch(stringCmFiltersLoadedAction(filters.filter(f => f.name === CmServiceFilterType.cmSearch)[0]));
      }

      if (!favoritesFilter?.name) {
        dispatch(favoriteCmFiltersLoadedAction(filters.filter(f => f.name === CmServiceFilterType.cmFavorites)[0]));
      }

      dispatch(selectedCmFiltersLoadedAction(models));

      const cmFilters = Object.values(models).map(f => ({
        serialNumber: f.serialNumber,
        name: f.name,
        format: 'LIST',
        inputValue: getCheckedValues(f)
      }));

      onChange([...cmFilters, searchStringFilter, favoritesFilter].filter(isCmFilter));
    }
  }, [filters]);

  const triggerOnChange = (filterRecord: Record<string, CmFilterModel>) => {
    const cmFilters = Object.values(filterRecord).reduce((acc, f) => {
      const checkedValues = getCheckedValues(f);

      if (checkedValues) {
        acc.push({
          serialNumber: f.serialNumber,
          name: f.name,
          format: 'LIST',
          inputValue: checkedValues
        });
      }

      return acc;
    }, [] as ICmFilter[]);

    onChange([...cmFilters, searchStringFilter, favoritesFilter].filter(isCmFilter));
  };

  const handleToggle = (type: string) => {
    return (id: number, checked: boolean) => {
      const updatedFilters = {...selectedFilters};

      if (updatedFilters[type]) {
        updatedFilters[type] = {
          ...updatedFilters[type],
          values: updatedFilters[type].values?.map(v => v.id === id ? {...v, checked} : v) ?? []
        };
      }

      dispatch(selectedCmFiltersLoadedAction(updatedFilters));
      triggerOnChange(updatedFilters);
    };
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      const cmFilters = Object.values(selectedFilters).map(f => ({
        serialNumber: f.serialNumber,
        name: f.name,
        format: 'LIST',
        inputValue: f.values?.filter(v => v.checked).map(v => v.id).join(',') || ''
      }));
      const newSearchStringFilter = {...searchStringFilter || {} as ICmFilter, inputValue: searchString};

      dispatch(stringCmFiltersLoadedAction(newSearchStringFilter));
      onChange([...cmFilters, newSearchStringFilter, favoritesFilter].filter(isCmFilter));
    }
  };

  const countyValues = selectedFilters?.[CmServiceFilterType.cmCounties]?.values ?? [];

  const concernValues = selectedFilters?.[CmServiceFilterType.cmDisorders]?.values ?? [];

  const hasActiveFilters = searchString !== ''
    || countyValues.some(v => v.checked)
    || concernValues.some(v => v.checked);

  const handleClearFilters = () => {
    const clearedFilters = Object.fromEntries(
      Object.entries(selectedFilters).map(([key, f]) => [key, {
        ...f,
        values: f.values?.map(v => ({...v, checked: false})),
        groups: f.groups?.map(g => ({...g, values: g.values.map(v => ({...v, checked: false}))}))
      }])
    );
    const clearedSearchFilter = {...searchStringFilter || {} as ICmFilter, inputValue: ''};

    setSearchString('');
    dispatch(selectedCmFiltersLoadedAction(clearedFilters));
    dispatch(stringCmFiltersLoadedAction(clearedSearchFilter));
    onChange([clearedSearchFilter, favoritesFilter].filter(isCmFilter));
  };

  const handleCountyToggle = (id: number, checked: boolean) => {
    const updatedFilters = {...selectedFilters};

    if (updatedFilters[CmServiceFilterType.cmCounties]) {
      updatedFilters[CmServiceFilterType.cmCounties] = {
        ...updatedFilters[CmServiceFilterType.cmCounties],
        values: updatedFilters[CmServiceFilterType.cmCounties].values?.map(v => v.id === id ? {...v, checked} : v) ?? []
      };
    }

    dispatch(selectedCmFiltersLoadedAction(updatedFilters));
    triggerOnChange(updatedFilters);
  };

  function renderCounties() {
    if (!countyValues.length) return null;

    const filteredCounties = countySearchText
      ? countyValues.filter(v => v.name.toLowerCase().includes(countySearchText.toLowerCase()))
      : countyValues;
    const selectedCount = countyValues.filter(v => v.checked).length;

    return (
      <div className="dm-resource-filters__counties" ref={countyRef}>
        <button
          type="button"
          className={`dm-resource-filters__counties-trigger${countyDropdownOpen ? ' dm-resource-filters__counties-trigger--open' : ''}`}
          onClick={() => setCountyDropdownOpen(prev => !prev)}
        >
          <span>
            {selectedCount > 0
              ? intl.formatMessage({id: 'gritx.cmresource.filter.countiesSelected', defaultMessage: '{count} selected'}, {count: selectedCount})
              : intl.formatMessage({id: 'gritx.cmresource.filter.allCounties', defaultMessage: 'All Counties'})
            }
          </span>
          <span className="dm-resource-filters__counties-chevron" />
        </button>
        {countyDropdownOpen && (
          <div className="dm-resource-filters__counties-dropdown">
            <input
              className="dm-resource-filters__counties-search"
              type="text"
              value={countySearchText}
              onChange={e => setCountySearchText(e.target.value)}
              placeholder={intl.formatMessage({id: 'gritx.cmresource.filter.countiesSearch', defaultMessage: 'Search counties...'})}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
            <div className="dm-resource-filters__counties-list">
              {filteredCounties.map(v => (
                <label key={v.id} className="dm-resource-filters__counties-option">
                  <input
                    type="checkbox"
                    checked={v.checked}
                    onChange={e => handleCountyToggle(v.id, e.target.checked)}
                  />
                  <span>{v.name}</span>
                </label>
              ))}
              {filteredCounties.length === 0 && (
                <div className="dm-resource-filters__counties-empty">
                  {intl.formatMessage({id: 'gritx.cmresource.filter.countiesNoResults', defaultMessage: 'No counties found'})}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderConcerns() {
    return <>
      {concernValues.length > 0 && (
        <div className="dm-resource-filters__tag-container">
          <div className="dm-resource-filters__tag-container-title">
            {intl.formatMessage({id: 'gritx.cmresource.filter.concern', defaultMessage: 'Concern'})}
          </div>
          <div className="dm-resource-filters__chips">
            {concernValues.map(v => (
              <DmFilter
                key={v.id}
                title={v.name}
                id={v.id}
                checked={v.checked}
                onToggle={handleToggle(CmServiceFilterType.cmDisorders)}
              />
            ))}
          </div>
        </div>
      )}
    </>;
  }

  function renderClearButton() {
    return <>
      {hasActiveFilters && (
        <button
          type="button"
          className="dm-resource-filters__clear-btn"
          onClick={handleClearFilters}
        >
          {intl.formatMessage({id: 'gritx.cmresource.filter.clear', defaultMessage: 'Clear filters'})}
        </button>
      )}
    </>;
  }

  return (
    <div className="dm-resource-filters">
      <div className="dm-resource-filters__top-row">
        <div className="dm-resource-filters__search">
          <Input
            className="dm-resource-filters__search-input"
            value={searchString}
            placeholder={intl.formatMessage({
              id: 'gritx.cmresource.search.placeholder',
              defaultMessage: 'Search resources by title, author, or tags...'
            })}
            onChange={(value: string) => setSearchString(value)}
            onKeyUp={handleSearchKeyPress}
          />
        </div>
        {/* County filter hidden for now: {renderCounties()} */}
      </div>

      {concernValues.length > 0 && (
        <div className="dm-resource-filters__tags-section">
          <div className="dm-resource-filters__tags-title">
            {intl.formatMessage({id: 'gritx.cmresource.filter.tagsTitle', defaultMessage: 'Filter by Tags'})}
          </div>

          <div className="dm-resource-filters__tag-containers">
            {renderConcerns()}
          </div>

        </div>
      )}

      {renderClearButton()}
    </div>
  );
};
